import { Box, Chip, Typography } from '@mui/material'
import { useAtomValue, useSetAtom } from 'jotai'
import type { MouseEvent } from 'react'
import {
  debouncedSearchQueryAtom,
  gatheringSidebarModeAtom,
  itemSearchQueryAtom,
  selectedGatheringAreaAtom,
} from '@/atoms'
import { useGatheringList } from '@/hooks/useGatheringList'
import { GATHERING_TYPE_LABEL } from '@/types/constants'
import type { GatherRecord } from '@/types'

const PROB_COLOR: Record<string, string> = {
  A: '#ef5350',
  B: '#ff9800',
  C: '#fbc02d',
  D: '#66bb6a',
  E: '#90a4ae',
}

function highlight(text: string, query?: string) {
  if (!query || !text.includes(query)) return <>{text}</>
  const idx = text.indexOf(query)
  return (
    <>
      {text.slice(0, idx)}
      <strong style={{ backgroundColor: '#fff176' }}>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </>
  )
}

function BaitList({ bait, onClickName }: { bait?: string[]; onClickName: (name: string) => void }) {
  if (!bait || bait.length === 0) return null

  function handleClick(event: MouseEvent, name: string) {
    event.stopPropagation()
    onClickName(name)
  }

  return (
    <Typography variant="caption" color="text.disabled">
      {bait.map((name, index) => (
        <Typography
          key={`${name}-${index}`}
          component="span"
          variant="caption"
          onClick={(event) => handleClick(event, name)}
          sx={{
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline', color: 'primary.main' },
          }}
        >
          {index > 0 && ' / '}
          {name}
        </Typography>
      ))}
    </Typography>
  )
}

function fishingLocation(record: GatherRecord) {
  return record.fishingSpot ? `${record.area}・${record.fishingSpot}` : record.area
}

const clickableSx = {
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline', color: 'primary.main' },
} as const

function RecordRow({
  record,
  highlightQuery,
  onClickName,
  onClickArea,
}: {
  record: GatherRecord
  highlightQuery?: string
  onClickName: (name: string) => void
  onClickArea: (area: string) => void
}) {
  const isFishing = record.gatheringType === 'fishing'
  const isChocoboDigging = record.gatheringType === 'chocoboDigging'

  if (isFishing) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '72px minmax(120px, 1fr) minmax(160px, 1fr) minmax(160px, 1fr)',
          gap: 1,
          alignItems: 'start',
          px: 1,
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'right' }}>
          {GATHERING_TYPE_LABEL.fishing}
          {record.skillLevel}
        </Typography>
        <Typography variant="caption" onClick={() => onClickName(record.name)} sx={clickableSx}>
          {highlight(record.name, highlightQuery)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          title={fishingLocation(record)}
          onClick={() => onClickArea(record.area)}
          sx={clickableSx}
        >
          {fishingLocation(record)}
        </Typography>
        <BaitList bait={record.bait} onClickName={onClickName} />
      </Box>
    )
  }

  if (isChocoboDigging) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(140px, 1fr) 88px minmax(120px, 0.8fr) minmax(160px, 1fr)',
          gap: 1,
          alignItems: 'start',
          px: 1,
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          onClick={() => onClickArea(record.area)}
          sx={clickableSx}
        >
          {record.area}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {record.skillLabel ?? '-'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {record.condition ?? '-'}
        </Typography>
        <Typography variant="caption" onClick={() => onClickName(record.name)} sx={clickableSx}>
          {highlight(record.name, highlightQuery)}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 28px 1fr',
        gap: 1,
        alignItems: 'start',
        px: 1,
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        onClick={() => onClickArea(record.area)}
        sx={clickableSx}
      >
        {record.area}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {record.probability !== undefined && (
          <Chip
            label={record.probability}
            size="small"
            sx={{
              bgcolor: PROB_COLOR[record.probability] ?? '#e0e0e0',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.65rem',
              height: 16,
              width: 20,
              '& .MuiChip-label': { px: 0.25 },
            }}
          />
        )}
      </Box>
      <Typography variant="caption" onClick={() => onClickName(record.name)} sx={clickableSx}>
        {highlight(record.name, highlightQuery)}
      </Typography>
    </Box>
  )
}

export function GatheringListPanel() {
  const records = useGatheringList()
  const query = useAtomValue(debouncedSearchQueryAtom)
  const setSearch = useSetAtom(itemSearchQueryAtom)
  const setGatheringArea = useSetAtom(selectedGatheringAreaAtom)
  const setGatheringSidebarMode = useSetAtom(gatheringSidebarModeAtom)
  const isFishing = records[0]?.gatheringType === 'fishing'
  const isChocoboDigging = records[0]?.gatheringType === 'chocoboDigging'

  function handleAreaClick(area: string) {
    setGatheringArea(area)
    setGatheringSidebarMode(isFishing ? 'item' : 'area')
  }

  return (
    <Box
      sx={{
        gridArea: 'main',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isFishing && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '72px minmax(120px, 1fr) minmax(160px, 1fr) minmax(160px, 1fr)',
            gap: 1,
            px: 1,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography variant="caption" color="text.disabled" fontWeight={700}>
            釣りレベル
          </Typography>
          <Typography variant="caption" color="text.disabled" fontWeight={700}>
            水産物
          </Typography>
          <Typography variant="caption" color="text.disabled" fontWeight={700}>
            ロケーション
          </Typography>
          <Typography variant="caption" color="text.disabled" fontWeight={700}>
            餌
          </Typography>
        </Box>
      )}

      {isChocoboDigging && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(140px, 1fr) 88px minmax(120px, 0.8fr) minmax(160px, 1fr)',
            gap: 1,
            px: 1,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography variant="caption" color="text.disabled" fontWeight={700}>
            場所
          </Typography>
          <Typography variant="caption" color="text.disabled" fontWeight={700}>
            必要スキル
          </Typography>
          <Typography variant="caption" color="text.disabled" fontWeight={700}>
            条件
          </Typography>
          <Typography variant="caption" color="text.disabled" fontWeight={700}>
            アイテム名
          </Typography>
        </Box>
      )}

      {records.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.disabled">データがありません</Typography>
        </Box>
      ) : (
        records.map((record) => (
          <RecordRow
            key={record.id}
            record={record}
            highlightQuery={query || undefined}
            onClickName={(name) => setSearch(name)}
            onClickArea={handleAreaClick}
          />
        ))
      )}
    </Box>
  )
}
